import type { CallPayerHours } from '@compass/client/types/call';
import momentTz from 'moment-timezone';

import type { SupportedTimeZone } from '@/constants/date';
import { TIMEZONE_MAP } from '@/utils/date';

const DAY_ABBREVIATIONS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS_WINDOW = /^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/;
const HOLIDAY_DATE = /^(\d{1,2})-(\d{1,2})-\d{4}$/;

export const PAYER_CLOSED_TODAY = 'Closed today';
export const PAYER_CLOSED_TODAY_HOLIDAY = 'Closed today (holiday)';

/** Parses "Mon=08:00-17:00;Tue=..." into day abbreviation -> "08:00-17:00". */
const parseWorkingHours = (workingHours: string): Map<string, string> => {
  const days = new Map<string, string>();
  for (const part of workingHours.split(';')) {
    const [day, span] = part.split('=').map(s => s.trim());
    if (day && span) days.set(day.slice(0, 3).toLowerCase(), span);
  }
  return days;
};

/** Holidays recur yearly (the policy engine matches month and day only). */
const isHoliday = (holidaysList: string, month: number, day: number): boolean =>
  holidaysList.split(',').some(entry => {
    const match = HOLIDAY_DATE.exec(entry.trim());
    return !!match && Number(match[1]) === month && Number(match[2]) === day;
  });

/**
 * Reads an "08:00-17:00" window on `date` in the payer's zone and formats it in
 * the viewer's, e.g. "5:00 AM – 2:00 PM". Null for a malformed window.
 */
const formatWindow = (
  span: string,
  date: string,
  payerZone: string,
  viewerZone: string,
): string | null => {
  const match = HOURS_WINDOW.exec(span);
  if (!match) return null;
  const at = (hour: string, minute: string) =>
    momentTz
      .tz(`${date} ${hour.padStart(2, '0')}:${minute}`, 'YYYY-MM-DD HH:mm', payerZone)
      .tz(viewerZone)
      .format('h:mm A');
  return `${at(match[1], match[2])} – ${at(match[3], match[4])}`;
};

/**
 * The payer's working hours for today, in the viewer's timezone, for the Call
 * tooltip's "Payer Hours" row (BV-2480).
 *
 * "Today" is the current date in the viewer's timezone. That calendar date is
 * looked up in the payer's own schedule (`payerHours.timezone`), and the
 * window is converted to the viewer's timezone: 08:00-17:00 Eastern reads
 * "8:00 AM – 5:00 PM" in ET and "5:00 AM – 2:00 PM" in PT. A holiday wins over
 * the weekday schedule; a day without hours is closed.
 *
 * Returns null when the row should be hidden: no payer hours, an unknown
 * timezone, or a malformed window.
 */
export const formatPayerHoursToday = (
  payerHours: CallPayerHours | undefined,
  timeZone: SupportedTimeZone,
  now: Date = new Date(),
): string | null => {
  const payerZone = payerHours?.timezone;
  const viewerZone = TIMEZONE_MAP[timeZone];
  if (!payerZone || !viewerZone || !momentTz.tz.zone(payerZone)) return null;

  const today = momentTz.tz(now, viewerZone);
  if (isHoliday(payerHours.holidays_list ?? '', today.month() + 1, today.date())) {
    return PAYER_CLOSED_TODAY_HOLIDAY;
  }

  const day = DAY_ABBREVIATIONS[today.day()].toLowerCase();
  const span = parseWorkingHours(payerHours.working_hours ?? '').get(day);
  if (!span) return PAYER_CLOSED_TODAY;

  return formatWindow(span, today.format('YYYY-MM-DD'), payerZone, viewerZone);
};
